/**
 * @file ChangesSheet.tsx
 * @description Шторка «Что изменено» (phase19.16 Р1; uiux-design-r1 §0.1,
 * §6): изменённые части черновика с автором и временем правки, строки
 * «было → стало», «Отменить» у части (только при праве правки части).
 * На телефоне — на весь экран, справа — на широком.
 */

import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { useConfirm } from "../../ui/use-confirm";
import { ChangeItemsList } from "../ChangeItemsList";
import { canOpenPublish, hasDraftChanges, useSiteView } from "../site-view-context";
import { useDiscard } from "../../../hooks/site-view/useDiscard";
import { formatEditMoment, partHeading } from "../../../lib/site-view/changes";
import type { SiteChangedPart } from "../../../lib/site-view/types";

function PartBlock({ part, onDiscard }: { part: SiteChangedPart; onDiscard: () => void }) {
  const who = part.editedBy ? ` (${part.editedBy.name}${part.editedAt ? `, ${formatEditMoment(part.editedAt)}` : ""})` : "";
  return (
    <section className="flex flex-col gap-2 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="type-card-title text-on-surface">
          {partHeading(part.label)} · {part.count}
          <span className="type-caption font-normal text-on-surface-variant">{who}</span>
        </h3>
        {part.canDiscard ? (
          <Button variant="ghost" onClick={onDiscard}>
            Отменить
          </Button>
        ) : null}
      </div>
      <ChangeItemsList items={part.items} />
    </section>
  );
}

export function ChangesSheet() {
  const view = useSiteView();
  const confirm = useConfirm();
  const { discard } = useDiscard();
  const open = view.dialog === "changes";
  const changes = view.changes;

  async function onDiscard(part: SiteChangedPart) {
    const ok = await confirm.confirm({
      title: `Отменить изменения: ${part.label}?`,
      description: "Черновик этой части вернётся к виду на сайте. Сайт не изменится.",
      action: "Отменить изменения",
      danger: true,
    });
    if (ok) await discard([part.part]);
  }

  return (
    <Sheet open={open} onOpenChange={(next) => view.openDialog(next ? "changes" : null)}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Что изменено</SheetTitle>
        </SheetHeader>
        {!changes || !hasDraftChanges(changes) ? (
          <p className="type-body text-on-surface-variant">Черновик совпадает с сайтом — публиковать нечего.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {changes.changedParts.map((part) => (
              <PartBlock key={part.part} part={part} onDiscard={() => void onDiscard(part)} />
            ))}
          </div>
        )}
        <div className="mt-auto grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => view.openDialog("preview")}>
            Предпросмотр
          </Button>
          <Button
            variant="brand"
            disabled={!canOpenPublish(changes)}
            onClick={() => view.openDialog("publish")}
          >
            Опубликовать
          </Button>
        </div>
        <ConfirmDialog request={confirm.request} onSettle={confirm.settle} />
      </SheetContent>
    </Sheet>
  );
}
